import { useCallback, useContext, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';

import { ProfileContext } from 'src/components/shared/AuthController';
import { useData } from 'src/hooks/useData';
import {
  ModelInstance,
  RowIdentifier,
  Scenario,
  Submission,
  SubmissionStatus,
  XRefDate,
} from 'src/types/models';
import { HTTPError } from 'src/support/errors';

import { dimensionHeaders as buildDimensionHeaders } from '../utils/dimensionHeaders';
import { buildPeriodLabeler } from '../utils/periodLabels';
import { parseResolveParams } from '../utils/routeParams';

const UNSET_VERSION = '?';

export type ResolveContext =
  | { kind: 'invalid-params' }
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'forbidden' }
  | { kind: 'error'; error: unknown; retry: () => void }
  | {
      kind: 'ready';
      ids: { instanceId: number; scenarioId: number };
      session: { groupRowIdentifierId: number; submissionId: number };
      labels: {
        scenarioName: string;
        baseVersion: string;
        myVersion: string;
        currentVersion: string;
        // Who saved each version and when, worded as the scenario history card
        // words it. This is what the header shows; the version codes are not.
        baseSaved: string;
        mySaved: string;
        currentSaved: string;
        groupName?: string;
      };
      dimensionHeaders: string[];
      periodLabel: (dateId: string | null) => string;
      // Not in the type literally handed down with this task, but required
      // by the "expose a setVersions callback" rule below — there is no
      // other channel for the page to reach it. Attached only to `ready`
      // because it is only meaningful once a session exists to label.
      setVersions: (baseId: number, currentId: number) => void;
    };

const stripLeadingV = (version: string): string => version.replace(/^v/, '');

// Same fields and date format as SubmissionCard's user line and "Created" line.
const savedBy = (submission: Submission | undefined): string =>
  submission
    ? `${submission.User?.Display_Name ?? 'Unknown'}, ${format(
        new Date(submission.created_at),
        'MMM. d, yyyy hh:mma'
      )}`
    : UNSET_VERSION;

export function useResolveContext(): ResolveContext {
  const location = useLocation();
  const profile = useContext(ProfileContext);

  const params = useMemo(
    () => parseResolveParams(location.search),
    [location.search]
  );

  // BaseSubmissionID/CurrentSubmissionID come from `merge/diff`, not from any
  // of this hook's own fetches — the page learns them after bootstrapping
  // the diff and reports them back here.
  const [versions, setVersionsState] = useState<{
    baseId: number;
    currentId: number;
  } | null>(null);

  const setVersions = useCallback((baseId: number, currentId: number) => {
    setVersionsState({ baseId, currentId });
  }, []);

  const { data, loading, errors, refresh } = useData<{
    scenario?: Scenario;
    modelInstance?: ModelInstance;
    dates?: XRefDate[];
    submissions?: Submission[];
    rowIdentifiers?: RowIdentifier[];
  }>(
    () => ({
      scenario: params
        ? `/instances/${params.instanceId}/scenarios/${params.scenarioId}`
        : undefined,
      modelInstance: params ? `/instances/${params.instanceId}` : undefined,
      dates: params ? `/xref_date/${params.clientId}` : undefined,
      submissions: params
        ? `/instances/${params.instanceId}/scenarios/${params.scenarioId}/submissions`
        : undefined,
      // The merge wire carries no dimension names (Task 46) — the only
      // source for them is the instance's own Dimension-type row
      // identifiers, the same place every other screen in this repo reads
      // dimension names from (see e.g. DriverAnalysis, Extracts). This is a
      // fifth fetch beyond the four the task calls out by name, added
      // because `dimensionHeaders()` is a pure formatter with no data of
      // its own — something has to supply the raw names.
      rowIdentifiers: params
        ? `/instances/${params.instanceId}/row_identifiers`
        : undefined,
    }),
    [params],
    // Force a reload: the submission list is cached from before the save that
    // created this submission, so a cache hit would not contain it.
    true
  );

  return useMemo<ResolveContext>(() => {
    if (!params) {
      return { kind: 'invalid-params' };
    }

    // Fail fast rather than waiting for every need to settle: once any one
    // of the five fetches has a definite HTTP failure, the screen cannot
    // reach `ready` regardless of what the others do, so there is no value
    // in holding the caller on `loading` until they all finish. Among
    // failures, 403 outranks 404 — an explicit permission denial is a more
    // specific, more actionable signal than "not found", so if a caller
    // somehow gets both for different needs, forbidden wins. Anything else
    // (5xx, network failure) falls through to a generic retryable error.
    if (errors.length > 0) {
      const httpErrors = errors.filter(
        (error): error is HTTPError => error instanceof HTTPError
      );

      if (httpErrors.some((error) => error.status === 403)) {
        return { kind: 'forbidden' };
      }

      if (httpErrors.some((error) => error.status === 404)) {
        return { kind: 'not-found' };
      }

      return { kind: 'error', error: errors[0], retry: refresh };
    }

    if (
      loading ||
      !data.scenario ||
      !data.modelInstance ||
      !data.dates ||
      !data.submissions ||
      !data.rowIdentifiers ||
      // AuthController never renders its children until a profile is
      // loaded (or shows its own Unauthorized screen on failure), so by the
      // time anything under it can mount this hook a profile should always
      // be present. Treating an absent one as `loading` rather than
      // crashing is a defensive fallback for that invariant, not an
      // expected path.
      !profile
    ) {
      return { kind: 'loading' };
    }

    const submission = data.submissions.find(
      (s) => s.id === params.submissionId
    );

    if (
      !submission ||
      submission.Status !== SubmissionStatus.PendingResolution ||
      submission.GroupRowIdentifierID === undefined
    ) {
      // Deliberate: `not-found`, not `forbidden`. A submission that is
      // missing or in the wrong state must look identical to one that was
      // never there at all. Ownership is not checked here: the API returns a
      // pending row only to users authorised to resolve it (CLD-3120).
      return { kind: 'not-found' };
    }

    const groupName = data.rowIdentifiers.find(
      (row) =>
        row.Type === 'Group' && row.id === submission.GroupRowIdentifierID
    )?.Name;

    const versionOf = (id: number): string | undefined => {
      const match = data.submissions?.find((s) => s.id === id);
      return match ? stripLeadingV(match.version) : undefined;
    };

    // Every version label defaults to "?" until the page calls
    // `setVersions()` after bootstrapping `merge/diff` — including
    // `myVersion`, even though the resolved submission's own version is
    // already known at this point. Kept atomic on purpose: the header reads
    // as one fact ("base vX, yours vY, current vZ"), and showing one real
    // number next to two placeholders would be a worse partial state than
    // showing three.
    const submissionOf = (id: number) =>
      data.submissions?.find((s) => s.id === id);
    const labels = versions
      ? {
          scenarioName: data.scenario.Name,
          baseVersion: versionOf(versions.baseId) ?? UNSET_VERSION,
          myVersion: versionOf(submission.id) ?? UNSET_VERSION,
          currentVersion: versionOf(versions.currentId) ?? UNSET_VERSION,
          baseSaved: savedBy(submissionOf(versions.baseId)),
          mySaved: savedBy(submission),
          currentSaved: savedBy(submissionOf(versions.currentId)),
          groupName,
        }
      : {
          scenarioName: data.scenario.Name,
          baseVersion: UNSET_VERSION,
          myVersion: UNSET_VERSION,
          currentVersion: UNSET_VERSION,
          baseSaved: UNSET_VERSION,
          mySaved: UNSET_VERSION,
          currentSaved: UNSET_VERSION,
          groupName,
        };

    const dimensionNames = data.rowIdentifiers
      .filter((row) => row.Type === 'Dimension')
      .sort((a, b) => (a.DimNumber ?? 0) - (b.DimNumber ?? 0))
      .map((row) => row.Name);

    return {
      kind: 'ready',
      ids: { instanceId: params.instanceId, scenarioId: params.scenarioId },
      session: {
        groupRowIdentifierId: submission.GroupRowIdentifierID,
        submissionId: submission.id,
      },
      labels,
      dimensionHeaders: buildDimensionHeaders(
        dimensionNames,
        dimensionNames.length
      ),
      periodLabel: buildPeriodLabeler(data.dates, data.modelInstance.Timescale),
      setVersions,
    };
  }, [params, data, loading, errors, profile, versions, refresh, setVersions]);
}
